import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { telecmiApi } from "../lib/api";
import { toast } from "sonner";
import CallCustomerButton from "./CallCustomerButton";
import {
  X,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Package,
  Headphones,
  Mail,
  MessageCircle,
  Calendar,
  Maximize2,
  ExternalLink,
  Sparkles,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Clock,
  AlertTriangle,
  CreditCard,
  Languages,
  User as UserIcon,
  Radio,
} from "lucide-react";

/**
 * Customer 360 unified view. Reusable as:
 *   • Right-side drawer (mode="drawer"): slides in from right, 720px wide
 *   • Full page (mode="page"): absorbs container width
 *
 * Loads /api/customer-360/:phone/full once on open. Tabs:
 *   Overview · Orders · Calls · Tickets · Chat
 *
 * Designed to be opened from anywhere — chat header, ringing popup, calls
 * page row, or as a deep-link `/customer/:phone`.
 */
const fmt = (sec) => {
  if (!sec || sec < 1) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const StatusPill = ({ status, palette = {} }) => {
  const s = String(status || "").toLowerCase();
  const colors = {
    open: "bg-blue-100 text-blue-700 border-blue-200",
    closed: "bg-gray-100 text-gray-600 border-gray-200",
    completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    answered: "bg-emerald-100 text-emerald-700 border-emerald-200",
    failed: "bg-red-100 text-red-700 border-red-200",
    missed: "bg-red-100 text-red-700 border-red-200",
    no_answer: "bg-amber-100 text-amber-700 border-amber-200",
    ringing: "bg-amber-100 text-amber-700 border-amber-200",
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
    delivered: "bg-emerald-100 text-emerald-700 border-emerald-200",
    shipped: "bg-blue-100 text-blue-700 border-blue-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
    ...palette,
  };
  const cls = colors[s] || "bg-gray-100 text-gray-700 border-gray-200";
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider border px-2 py-0.5 rounded-full ${cls}`}>
      {status || "—"}
    </span>
  );
};

// ============ TAB COMPONENTS ============

const OverviewTab = ({ data }) => {
  const lead = data?.lead || {};
  const totals = data?.totals || {};
  const ltv = data?.ltv || 0;
  return (
    <div className="space-y-3">
      {/* KPI grid */}
      <div className="grid grid-cols-4 gap-2" data-testid="c360-kpis">
        <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 text-center">
          <div className="text-xl font-extrabold text-orange-700">{totals.orders || 0}</div>
          <div className="text-[10px] text-orange-600 uppercase font-bold">Orders</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
          <div className="text-xl font-extrabold text-emerald-700">₹{Math.round((ltv || 0) / 1000)}k</div>
          <div className="text-[10px] text-emerald-600 uppercase font-bold">LTV</div>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
          <div className="text-xl font-extrabold text-blue-700">{totals.calls || 0}</div>
          <div className="text-[10px] text-blue-600 uppercase font-bold">Calls</div>
        </div>
        <div className="bg-purple-50 border border-purple-100 rounded-xl p-3 text-center">
          <div className="text-xl font-extrabold text-purple-700">{totals.open_tickets || 0}</div>
          <div className="text-[10px] text-purple-600 uppercase font-bold">Open tickets</div>
        </div>
      </div>

      {/* Profile */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2 text-[12px]" data-testid="c360-profile">
        <div className="flex items-center justify-between"><span className="text-gray-500">Name</span><span className="font-bold">{lead.name || "—"}</span></div>
        <div className="flex items-center justify-between"><span className="text-gray-500">Phone</span><span className="font-mono">{lead.phone || "—"}</span></div>
        {lead.customer_email && (
          <div className="flex items-center justify-between"><span className="text-gray-500">Email</span><span className="font-mono text-[11px]">{lead.customer_email}</span></div>
        )}
        <div className="flex items-center justify-between"><span className="text-gray-500">Country</span><span>{lead.country || "—"}</span></div>
        <div className="flex items-center justify-between"><span className="text-gray-500">WATI status</span><StatusPill status={lead.wati_status} /></div>
        <div className="flex items-center justify-between"><span className="text-gray-500">Department</span><span className="font-bold">{lead.department || "—"}</span></div>
        <div className="flex items-center justify-between"><span className="text-gray-500">Last agent</span><span>{data?.last_agent?.name || "—"}</span></div>
        {Array.isArray(lead.issue_tags) && lead.issue_tags.length > 0 && (
          <div className="pt-1 border-t border-gray-100">
            <div className="text-[10px] text-gray-500 uppercase mb-1">Issue tags</div>
            <div className="flex flex-wrap gap-1">
              {lead.issue_tags.map(t => <span key={t} className="text-[10px] bg-pink-50 text-pink-700 border border-pink-200 px-1.5 py-0.5 rounded-full">{t}</span>)}
            </div>
          </div>
        )}
      </div>

      {/* Open follow-ups */}
      {(data?.follow_ups || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="c360-followups">
          <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold uppercase text-amber-700"><Calendar className="w-3.5 h-3.5" /> Open follow-ups</div>
          <ul className="space-y-1 text-[12px]">
            {data.follow_ups.slice(0, 5).map((f, i) => (
              <li key={f.id || i} className="flex items-center justify-between">
                <span className="text-amber-900">{f.note || f.title || "Follow-up"}</span>
                <span className="text-[10px] text-amber-600 font-mono">{fmtDate(f.reminder_date || f.scheduled_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const OrdersTab = ({ data }) => {
  const orders = data?.orders || [];
  if (orders.length === 0) return <div className="text-[12px] text-gray-500 py-8 text-center" data-testid="c360-orders-empty">No orders for this customer yet.</div>;
  return (
    <div className="space-y-2" data-testid="c360-orders">
      {orders.map((o) => (
        <div key={o.id || o.order_number} className="bg-white border border-gray-200 rounded-xl p-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-orange-500" />
              <span className="font-bold text-[13px]">{o.order_number || o.id || "—"}</span>
            </div>
            <StatusPill status={o.status} />
          </div>
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <div>
              <div className="text-gray-500">Amount</div>
              <div className="font-bold">₹{Number(o.amount || 0).toLocaleString("en-IN")}</div>
            </div>
            <div>
              <div className="text-gray-500">Created</div>
              <div className="font-mono text-[10px]">{fmtDate(o.created_at)}</div>
            </div>
            <div>
              <div className="text-gray-500">Tracking</div>
              <div className="truncate" title={o.tracking_number}>{o.tracking_number || "—"}</div>
            </div>
          </div>
          {o.items && Array.isArray(o.items) && o.items.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="text-[10px] text-gray-500 uppercase mb-1">Items</div>
              <ul className="text-[11px] space-y-0.5">
                {o.items.slice(0, 5).map((it, i) => (
                  <li key={`${o.id || o.order_number}-item-${i}`} className="flex items-start gap-1">
                    <ChevronRight className="w-3 h-3 mt-0.5 text-gray-400 shrink-0" />
                    <span>{it.name || it.title || it.product_name || "Item"} {it.qty ? `×${it.qty}` : ""}</span>
                  </li>
                ))}
                {o.items.length > 5 && <li className="text-[10px] text-gray-400">+{o.items.length - 5} more</li>}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const CallsTab = ({ data }) => {
  const calls = data?.calls || [];
  const [openIds, setOpenIds] = useState(new Set());
  const toggle = (id) => setOpenIds((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  if (calls.length === 0) return <div className="text-[12px] text-gray-500 py-8 text-center" data-testid="c360-calls-empty">No previous calls found.</div>;
  return (
    <div className="space-y-2" data-testid="c360-calls">
      {calls.map((c) => {
        const isOpen = openIds.has(c.id);
        const Icon = c.direction === "outbound" ? PhoneOutgoing : (String(c.status || "").toLowerCase().includes("miss") ? PhoneMissed : PhoneIncoming);
        return (
          <div key={c.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="p-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${c.direction === "outbound" ? "text-blue-600" : "text-emerald-600"}`} />
                  <span className="font-bold text-[12px] capitalize">{c.direction || "call"}</span>
                  <span className="text-[10px] text-gray-500 font-mono">{fmtDate(c.created_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={c.status} />
                  <span className="text-[11px] text-gray-700 font-bold">{fmt(c.duration)}</span>
                </div>
              </div>
              {c.agent_name && <div className="text-[11px] text-gray-600 mb-1">Agent: <span className="font-bold">{c.agent_name}</span></div>}
              {c.ai_summary && (
                <div className="bg-purple-50 border border-purple-100 rounded-lg p-2 mt-1" data-testid={`c360-call-summary-${c.id}`}>
                  <div className="flex items-center gap-1 text-[10px] text-purple-700 uppercase font-bold mb-1"><Sparkles className="w-3 h-3" /> AI summary</div>
                  <div className="text-[11px] whitespace-pre-line text-purple-900">{c.ai_summary}</div>
                  {c.ai_disposition && <div className="text-[10px] text-purple-600 mt-1">Disposition: <b>{c.ai_disposition}</b></div>}
                </div>
              )}
              {c.recording_url && (
                <div className="mt-1.5 bg-slate-50 border border-slate-200 rounded-lg p-1.5" data-testid={`c360-call-recording-${c.id}`}>
                  <div className="flex items-center gap-1 text-[10px] text-slate-600 font-bold mb-1">
                    <Headphones className="w-3 h-3" /> Call recording
                  </div>
                  <audio
                    controls
                    preload="none"
                    className="w-full h-7"
                    style={{ height: "28px" }}
                  >
                    <source src={c.recording_url} />
                    Your browser does not support audio playback.
                  </audio>
                  <a
                    href={c.recording_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-slate-500 hover:text-slate-800 underline mt-0.5 inline-block"
                  >
                    Open in new tab / download
                  </a>
                </div>
              )}
              {c.transcript && (
                <button
                  onClick={() => toggle(c.id)}
                  className="text-[11px] text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 mt-1.5 ml-3"
                  data-testid={`c360-call-transcript-toggle-${c.id}`}
                >
                  <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`} />
                  {isOpen ? "Hide transcript" : "Show transcript"}
                </button>
              )}
              {isOpen && c.transcript && (
                <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-2 text-[11px] whitespace-pre-line max-h-60 overflow-auto" data-testid={`c360-call-transcript-${c.id}`}>
                  {c.transcript}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TicketsTab = ({ data }) => {
  const tickets = data?.tickets || [];
  if (tickets.length === 0) return <div className="text-[12px] text-gray-500 py-8 text-center" data-testid="c360-tickets-empty">No support tickets linked.</div>;
  const opens = tickets.filter(t => (t.status || "").toLowerCase() === "open");
  const closed = tickets.filter(t => (t.status || "").toLowerCase() !== "open");
  return (
    <div className="space-y-3" data-testid="c360-tickets">
      {opens.length > 0 && (
        <div>
          <div className="text-[11px] uppercase font-bold text-blue-700 mb-1.5 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Open ({opens.length})</div>
          <div className="space-y-1.5">
            {opens.map(t => <TicketRow key={t.id} t={t} />)}
          </div>
        </div>
      )}
      {closed.length > 0 && (
        <div>
          <div className="text-[11px] uppercase font-bold text-gray-600 mb-1.5 flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Recently closed</div>
          <div className="space-y-1.5">
            {closed.map(t => <TicketRow key={t.id} t={t} />)}
          </div>
        </div>
      )}
    </div>
  );
};

const TicketRow = ({ t }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-start gap-3">
    <Mail className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <span className="font-bold text-[12px]">#{t.ticketNumber || t.id}</span>
        <StatusPill status={t.status} />
      </div>
      <div className="text-[12px] text-gray-800 truncate" title={t.subject}>{t.subject || "(no subject)"}</div>
      <div className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-2">
        <span className="font-mono">{fmtDate(t.modifiedTime || t.createdTime)}</span>
        {t.web_url && <a href={t.web_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-2.5 h-2.5" /> open</a>}
      </div>
    </div>
  </div>
);

const ChatTab = ({ data }) => {
  const messages = data?.messages || [];
  if (messages.length === 0) return <div className="text-[12px] text-gray-500 py-8 text-center" data-testid="c360-chat-empty">No WhatsApp messages yet.</div>;
  return (
    <div className="space-y-1" data-testid="c360-chat">
      {messages.map((m, i) => {
        const incoming = m.direction === "incoming";
        const internal = m.internal_only || m.direction === "system";
        return (
          <div key={m.id || `${m.timestamp}-${i}`} className={`flex ${incoming ? "justify-start" : "justify-end"}`}>
            <div className={`max-w-[80%] text-[12px] rounded-2xl px-3 py-1.5 ${
              internal ? "bg-amber-50 border border-amber-200 text-amber-900 italic" :
              incoming ? "bg-gray-100 text-gray-900" : "bg-emerald-50 border border-emerald-100 text-emerald-900"
            }`}>
              {!incoming && m.author_name && <div className="text-[9px] font-bold text-gray-500 mb-0.5">{m.author_name}</div>}
              <div className="whitespace-pre-line break-words">{m.content || (m.media_url ? "[media]" : "—")}</div>
              <div className="text-[9px] text-gray-400 text-right mt-0.5">{fmtDate(m.timestamp)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const URGENCY_THEME = {
  high: { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: "text-red-600" },
  medium: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", icon: "text-amber-600" },
  low: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", icon: "text-blue-600" },
};

const LiveTab = ({ liveCall }) => {
  const transcriptEnd = useRef(null);
  const coachEnd = useRef(null);
  useEffect(() => {
    transcriptEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [liveCall?.transcript?.length]);
  useEffect(() => {
    coachEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [liveCall?.coaching?.length]);

  if (!liveCall) {
    return (
      <div className="text-[12px] text-gray-500 py-8 text-center" data-testid="c360-live-empty">
        <Radio className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        No active call right now.
        <p className="text-[10px] text-gray-400 mt-1">When a call starts for this customer, the live transcript and AI coaching will appear here.</p>
      </div>
    );
  }

  const transcript = liveCall.transcript || [];
  const coaching = liveCall.coaching || [];

  return (
    <div className="space-y-3" data-testid="c360-live">
      {/* Status banner */}
      <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="c360-live-status">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-bold text-emerald-800">Call active{liveCall.is_mock ? " (mock)" : ""}</div>
          <div className="text-[10px] text-emerald-600">
            {liveCall.direction || "—"} · agent {liveCall.agent_name || "—"}
            {liveCall.muted ? " · muted" : ""}{liveCall.on_hold ? " · on hold" : ""}
          </div>
        </div>
      </div>

      {/* Transcript */}
      <div>
        <div className="text-[10px] uppercase font-bold text-gray-500 mb-1.5 flex items-center gap-1.5">
          <MessageCircle className="w-3 h-3" /> Live transcript ({transcript.length})
        </div>
        {transcript.length === 0 ? (
          <div className="text-[11px] text-gray-400 py-4 text-center bg-gray-50 border border-gray-200 rounded-lg">
            Listening… transcript appears live.
          </div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-auto bg-gray-50 border border-gray-200 rounded-lg p-2" data-testid="c360-live-transcript">
            {transcript.map((u, i) => (
              <div key={`${u.ts}-${i}`} className={`flex ${u.speaker === "agent" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] text-[11px] rounded-lg px-2 py-1 ${
                  u.speaker === "agent" ? "bg-emerald-100 text-emerald-900" :
                  u.speaker === "system" ? "bg-amber-50 text-amber-900 italic" :
                  "bg-white border border-gray-200"
                }`}>
                  <div className="text-[9px] uppercase font-bold opacity-70 mb-0.5">{u.speaker}</div>
                  {u.text}
                </div>
              </div>
            ))}
            <div ref={transcriptEnd} />
          </div>
        )}
      </div>

      {/* AI Coaching */}
      <div>
        <div className="text-[10px] uppercase font-bold text-gray-500 mb-1.5 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-purple-500" /> AI coaching ({coaching.length})
        </div>
        {coaching.length === 0 ? (
          <div className="text-[11px] text-gray-400 py-4 text-center bg-gray-50 border border-gray-200 rounded-lg">
            Claude is listening… first nudge in a few seconds.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="c360-live-coaching">
            {coaching.map((c, i) => {
              const cc = URGENCY_THEME[c.urgency] || URGENCY_THEME.low;
              return (
                <div key={`${c.ts}-${i}`} className={`text-[11px] ${cc.bg} border ${cc.border} ${cc.text} rounded-lg p-2`}>
                  <div className="flex items-start gap-1.5">
                    <Sparkles className={`w-3 h-3 mt-0.5 shrink-0 ${cc.icon}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold">{c.tip}</div>
                      <div className="text-[9px] uppercase tracking-wider mt-0.5 text-gray-500">{c.category} · {c.urgency}</div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={coachEnd} />
          </div>
        )}
      </div>
    </div>
  );
};

// ============ MAIN PANEL ============

const TABS = [
  { key: "overview", label: "Overview", Icon: UserIcon, Body: OverviewTab },
  { key: "live", label: "Live", Icon: Radio, Body: LiveTab },
  { key: "orders", label: "Orders", Icon: Package, Body: OrdersTab },
  { key: "calls", label: "Calls", Icon: Phone, Body: CallsTab },
  { key: "tickets", label: "Tickets", Icon: Mail, Body: TicketsTab },
  { key: "chat", label: "Chat", Icon: MessageCircle, Body: ChatTab },
];

const Customer360Panel = ({ phone, onClose, mode = "drawer" }) => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [liveCall, setLiveCall] = useState(null);
  const autoJumpedRef = useRef(false);

  useEffect(() => {
    if (!phone) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await telecmiApi.customer360Full(phone);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || "Failed to load customer profile");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [phone]);

  // Poll for an active live call on this phone every 3s
  useEffect(() => {
    if (!phone) return;
    let mounted = true;
    const fetchLive = async () => {
      try {
        const r = await telecmiApi.liveActiveByPhone(phone);
        if (!mounted) return;
        const newCall = r.data?.call || null;
        setLiveCall(newCall);
        // Auto-jump to Live tab the first time a call appears
        if (newCall && !autoJumpedRef.current) {
          autoJumpedRef.current = true;
          setTab("live");
        }
        if (!newCall) {
          autoJumpedRef.current = false;
        }
      } catch {
        // Silent — don't error-toast on a poll loop
      }
    };
    fetchLive();
    const t = setInterval(fetchLive, 3000);
    return () => { mounted = false; clearInterval(t); };
  }, [phone]);

  const Body = TABS.find(t => t.key === tab)?.Body || OverviewTab;
  const lead = data?.lead || {};
  const initials = (data?.name || "?").trim().charAt(0).toUpperCase();

  const inner = (
    <div className="flex flex-col h-full" data-testid="customer-360-panel">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-yellow-50">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white flex items-center justify-center text-xl font-extrabold shadow-md shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-extrabold truncate" data-testid="c360-name">{data?.name || phone}</h2>
            {data?.ltv >= 50000 && <span className="text-[9px] bg-yellow-50 text-yellow-700 border border-yellow-200 font-bold px-1.5 py-0.5 rounded-full uppercase">VIP</span>}
            {(data?.totals?.orders || 0) >= 2 && <span className="text-[9px] bg-purple-50 text-purple-700 border border-purple-200 font-bold px-1.5 py-0.5 rounded-full uppercase">Repeat</span>}
            {lead.country && <span className="text-[9px] bg-blue-50 text-blue-700 border border-blue-200 font-bold px-1.5 py-0.5 rounded-full uppercase flex items-center gap-1"><Languages className="w-2.5 h-2.5" /> {lead.country}</span>}
          </div>
          <p className="text-[12px] text-gray-500 font-mono">{data?.phone || phone}</p>
        </div>
        <div className="flex items-center gap-1">
          <CallCustomerButton
            phone={data?.phone || phone}
            customerName={data?.name}
            leadId={lead.id}
          />
          {mode === "drawer" && (
            <button
              onClick={() => { nav(`/customer/${encodeURIComponent(phone)}`); onClose && onClose(); }}
              title="Open full page"
              className="text-gray-500 hover:text-gray-900 p-1.5 rounded hover:bg-white/60"
              data-testid="c360-fullpage-btn"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
          {lead.id && (
            <button
              onClick={() => { nav(`/chats?lead=${lead.id}`); onClose && onClose(); }}
              title="Open in chats"
              className="text-orange-600 hover:text-orange-800 px-2 py-1 rounded hover:bg-white/60 text-[11px] font-bold flex items-center gap-1"
              data-testid="c360-open-chat-btn"
            >
              <ExternalLink className="w-3 h-3" /> Open chat
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="text-gray-500 hover:text-gray-900 p-1.5 rounded hover:bg-white/60" data-testid="c360-close-btn">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-3 pt-3 border-b border-gray-200 overflow-auto">
        {TABS.map(({ key, label, Icon }) => {
          const totals = data?.totals || {};
          const counts = { orders: totals.orders, calls: totals.calls, tickets: totals.open_tickets, chat: totals.messages };
          const count = counts[key];
          const isLiveTab = key === "live";
          const liveActive = isLiveTab && !!liveCall;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-3 py-2 text-[12px] font-bold rounded-t-lg flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap ${
                tab === key
                  ? (isLiveTab && liveActive ? "border-emerald-500 text-emerald-600 bg-emerald-50/40" : "border-orange-500 text-orange-600 bg-orange-50/40")
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
              data-testid={`c360-tab-${key}`}
            >
              {liveActive ? (
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
              ) : (
                <Icon className="w-3.5 h-3.5" />
              )}
              {label}
              {count > 0 && <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${tab === key ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-500"}`}>{count}</span>}
              {isLiveTab && liveActive && (
                <span className="text-[9px] bg-emerald-100 text-emerald-700 font-extrabold px-1.5 py-0.5 rounded-full uppercase">Live</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-4 bg-gray-50/40">
        {loading ? (
          <div className="text-center text-gray-500 text-[12px] py-12" data-testid="c360-loading">Loading customer 360…</div>
        ) : (
          <Body data={data} liveCall={liveCall} />
        )}
      </div>
    </div>
  );

  if (mode === "page") {
    return (
      <div className="h-full bg-white" data-testid="customer-360-page">
        {inner}
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-[200]" data-testid="customer-360-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-[720px] max-w-[95vw] bg-white shadow-2xl animate-in slide-in-from-right-4 duration-200">
        {inner}
      </div>
    </div>
  );
};

export default Customer360Panel;
